import React, { useEffect, useState, useTransition } from "react";
import {
  Bot,
  Filter,
  RefreshCw,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { fetchConversationBots, fetchConversationOverview } from "../api";
import { useDashboard } from "../context/DashboardContext";
import type { BotMetadata, ConversationOverviewPayload } from "../types";
import { ConversationKpiGrid } from "../components/conversations/ConversationKpiGrid";
import { ConversationDistributionChart } from "../components/conversations/ConversationDistributionChart";
import { ConversationDailyTrendChart } from "../components/conversations/ConversationDailyTrendChart";
import { ConversationAgentTable } from "../components/conversations/ConversationAgentTable";
import { ConversationLostReasonsTable } from "../components/conversations/ConversationLostReasonsTable";
import { ConversationReviewPanel } from "../components/conversations/ConversationReviewPanel";

export const ConversationsPage: React.FC = () => {
  const { selectedClient } = useDashboard();
  const effectiveClient = selectedClient || "ALL";

  // Filtros
  const [bots, setBots] = useState<BotMetadata[]>([]);
  const [selectedBotId, setSelectedBotId] = useState<string>("ALL");
  const [selectedChannel, setSelectedChannel] = useState<string>("ALL");
  const [includeTest, setIncludeTest] = useState<boolean>(false);

  // Fechas (por defecto ultimos 30 dias incluyendo hoy)
  const todayStr = new Date().toISOString().split("T")[0];
  const d30AgoStr = new Date(Date.now() - 30 * 86400000).toISOString().split("T")[0];

  const [startDate, setStartDate] = useState<string>(d30AgoStr);
  const [endDate, setEndDate] = useState<string>(todayStr);

  // Estado de carga y datos
  const [overview, setOverview] = useState<ConversationOverviewPayload | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [, startTransition] = useTransition();

  // Atajos de fechas
  const setQuickRange = (days: number) => {
    const end = todayStr;
    const start = new Date(Date.now() - days * 86400000).toISOString().split("T")[0];
    setStartDate(start);
    setEndDate(end);
  };

  // Cargar catálogo de bots al cambiar de cliente
  useEffect(() => {
    let active = true;
    fetchConversationBots(effectiveClient)
      .then((data) => {
        if (!active) return;
        setBots(data);
        if (data.length > 0 && selectedBotId !== "ALL" && !data.some((b) => b.bot_id === selectedBotId)) {
          setSelectedBotId("ALL");
        }
      })
      .catch((err) => {
        if (!active) return;
        console.error("Error al cargar bots:", err);
      });

    return () => {
      active = false;
    };
  }, [effectiveClient]);

  // Cargar overview de conversaciones
  const loadData = () => {
    setLoading(true);
    setError(null);
    fetchConversationOverview(effectiveClient, {
      botId: selectedBotId,
      channel: selectedChannel,
      startDate: startDate,
      endDate: endDate,
      includeTest: includeTest,
    })
      .then((payload) => {
        startTransition(() => {
          setOverview(payload);
          setLoading(false);
        });
      })
      .catch((err) => {
        console.error("Error al cargar métricas de conversaciones:", err);
        setError(err.message || "Error consultando BigQuery");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadData();
  }, [effectiveClient, selectedBotId, selectedChannel, startDate, endDate, includeTest]);

  const selectedBotMeta = bots.find((b) => b.bot_id === selectedBotId);
  const activeTimezone = selectedBotMeta?.timezone || "America/Santiago";

  return (
    <div className="conversations-page" style={{ padding: "24px 32px", maxWidth: "1400px", margin: "0 auto" }}>
      {/* 1. Título */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ padding: "8px", borderRadius: "8px", background: "rgba(59, 130, 246, 0.1)", color: "#3b82f6" }}>
              <Bot size={24} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: "22px", fontWeight: 700, color: "var(--text-primary, #f9fafb)" }}>
                Métricas de conversaciones
              </h1>
              <p style={{ margin: "2px 0 0", fontSize: "13px", color: "var(--text-muted, #9ca3af)" }}>
                Analítica omnicanal (WhatsApp · Telegram · Web) · Cliente: <strong style={{ color: "var(--text-primary)" }}>{effectiveClient === "ALL" ? "Todos" : effectiveClient}</strong>
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={loadData}
          disabled={loading}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            borderRadius: "6px",
            background: "rgba(255, 255, 255, 0.05)",
            border: "1px solid var(--border-subtle, rgba(255,255,255,0.1))",
            color: "var(--text-primary, #f9fafb)",
            fontSize: "13px",
            cursor: loading ? "not-allowed" : "pointer",
            fontWeight: 500,
          }}
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          <span>{loading ? "Actualizando..." : "Actualizar"}</span>
        </button>
      </div>

      {/* 2. Barra de Filtros (§6) */}
      <div
        style={{
          background: "var(--bg-surface, #111827)",
          border: "1px solid var(--border-subtle, rgba(255,255,255,0.08))",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "24px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "var(--text-muted)" }}>
          <Filter size={16} />
          <span style={{ fontSize: "13px", fontWeight: 600 }}>Filtros:</span>
        </div>

        {/* Selector de Pack/Bot */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Pack / Bot</label>
          <select
            value={selectedBotId}
            onChange={(e) => setSelectedBotId(e.target.value)}
            style={{
              padding: "6px 10px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          >
            <option value="ALL">Todos los packs ({bots.length})</option>
            {bots.map((b) => (
              <option key={b.bot_id} value={b.bot_id}>
                {b.bot_name || b.bot_id} ({b.mode})
              </option>
            ))}
          </select>
        </div>

        {/* Selector de Canal */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Canal</label>
          <select
            value={selectedChannel}
            onChange={(e) => setSelectedChannel(e.target.value)}
            style={{
              padding: "6px 10px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          >
            <option value="ALL">Todos los canales</option>
            <option value="wa">WhatsApp (wa)</option>
            <option value="tg">Telegram (tg)</option>
            <option value="web">Web Simulador (web)</option>
          </select>
        </div>

        {/* Rango de Fechas */}
        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Desde</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{
              padding: "5px 8px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          />
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <label style={{ fontSize: "11px", textTransform: "uppercase", color: "var(--text-muted)" }}>Hasta</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={{
              padding: "5px 8px",
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              borderRadius: "6px",
              color: "var(--text-primary)",
              fontSize: "13px",
            }}
          />
        </div>

        {/* Atajos Rápidos */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: "6px", height: "100%", paddingTop: "18px" }}>
          <button
            type="button"
            onClick={() => setQuickRange(7)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            7d
          </button>
          <button
            type="button"
            onClick={() => setQuickRange(30)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            30d
          </button>
          <button
            type="button"
            onClick={() => setQuickRange(90)}
            style={{
              padding: "5px 10px",
              borderRadius: "4px",
              background: "rgba(255,255,255,0.06)",
              border: "1px solid rgba(255,255,255,0.1)",
              color: "var(--text-primary)",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            90d
          </button>
        </div>

        {/* Checkbox Incluir Pruebas */}
        <div style={{ display: "flex", alignItems: "center", gap: "6px", paddingTop: "18px", marginLeft: "auto" }}>
          <input
            type="checkbox"
            id="include-test-check"
            checked={includeTest}
            onChange={(e) => setIncludeTest(e.target.checked)}
            style={{ cursor: "pointer" }}
          />
          <label htmlFor="include-test-check" style={{ fontSize: "12px", color: "var(--text-muted)", cursor: "pointer" }}>
            Incluir pruebas (web)
          </label>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px",
            borderRadius: "8px",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            color: "#f87171",
            marginBottom: "24px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {loading && !overview && (
        <div style={{ padding: "64px 16px", textAlign: "center", color: "var(--text-muted, #9ca3af)" }}>
          <RefreshCw size={24} className="spin" style={{ marginBottom: "12px" }} />
          <p style={{ margin: 0, fontSize: "14px" }}>Consultando conversaciones en BigQuery...</p>
        </div>
      )}

      {overview && (
        <>
          {/* 3 y 4: Fila de Tarjetas KPI + Aviso Contextual de Ventana */}
          <ConversationKpiGrid
            kpis={overview.kpis}
            capabilities={overview.capabilities}
          />

          {/* 5: Cómo terminaron */}
          <ConversationDistributionChart
            distribution={overview.distribution}
          />

          {/* 6: Por día */}
          <ConversationDailyTrendChart
            data={overview.daily}
            dateRange={overview.date_range}
            timezone={activeTimezone}
          />

          {/* 7: Por vendedor */}
          <ConversationAgentTable
            agents={overview.agents}
          />

          {/* 8: Por qué se fueron */}
          <ConversationLostReasonsTable
            lostReasons={overview.lost_reasons}
          />

          {/* 9: Curación */}
          <ConversationReviewPanel
            curation={overview.curation}
          />
        </>
      )}
    </div>
  );
};
